import React, { useState } from "react";
import {
  Typography,
  Button,
  Box,
  Grid,
  Container,
  Chip,
  Paper,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  InputAdornment,
  Tooltip,
  Fade,
} from "@mui/material";
import {
  Work,
  LocationOn,
  Schedule,
  Search,
  Close,
  ArrowForward,
  CheckCircle,
  People,
  TrendingUp,
  EmojiEvents,
  Favorite,
  School,
  HealthAndSafety,
  Laptop,
  FlightTakeoff,
  FitnessCenter,
  Star,
  Send,
  Bookmark,
  BookmarkBorder,
  FilterList,
  Groups,
  WorkHistory,
  CurrencyRupee,
  AutoAwesome,
  Verified,
} from "@mui/icons-material";
import Footer from "./Footer";
import jobs from "./jobs";
import ApplyDialog from "./ApplyDialog";

export const LandingPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDept, setSelectedDept] = useState("All");
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [savedJobs, setSavedJobs] = useState([]);

  const departments = [
    "All",
    "Sales",
    "Marketing",
    "Operations",
    "HR",
  ];

  const perks = [
    { icon: <HealthAndSafety sx={{ fontSize: 36 }} />, title: "Health Insurance", desc: "Complete medical, dental & vision coverage for you and your family.", color: "#10b981" },
    { icon: <FlightTakeoff sx={{ fontSize: 36 }} />, title: "Unlimited PTO", desc: "Flexible leave policy — take time when you need it, no questions asked.", color: "#0ea5e9" },
    { icon: <Laptop sx={{ fontSize: 36 }} />, title: "Remote Flexibility", desc: "Work from home, office, or anywhere. We trust you to deliver.", color: "#6366f1" },
    { icon: <School sx={{ fontSize: 36 }} />, title: "Learning Budget", desc: "₹50,000/year to spend on courses, books, conferences, or certifications.", color: "#f59e0b" },
    { icon: <FitnessCenter sx={{ fontSize: 36 }} />, title: "Wellness Perks", desc: "Gym memberships, mental health support, and wellness subscriptions covered.", color: "#d946ef" },
    { icon: <EmojiEvents sx={{ fontSize: 36 }} />, title: "ESOP / Equity", desc: "Be an owner. All full-time employees receive stock options.", color: "#ef4444" },
  ];

  const testimonials = [
    {
      name: "Kavitha Suresh",
      role: "Account Executive",
      joined: "Joined 2022",
      avatar: "KS",
      color: "#10b981",
      text: "In my first year at OruClick I exceeded quota by 140% and was promoted ahead of schedule. The sales culture here is competitive but incredibly supportive.",
    },
    {
      name: "Arjun Nair",
      role: "Business Development Manager",
      joined: "Joined 2023",
      avatar: "AN",
      color: "#0ea5e9",
      text: "The leadership gives you real ownership. I closed our first enterprise partnership within 3 months and the entire company celebrated it together — that says a lot about the culture.",
    },
    {
      name: "Meenakshi Rajan",
      role: "Customer Success Manager",
      joined: "Joined 2023",
      avatar: "MR",
      color: "#d946ef",
      text: "I genuinely love building relationships with our customers. OruClick empowers me with the tools and autonomy to make customers successful, and the recognition I get here is unmatched.",
    },
  ];

  const stats = [
    { value: "200+", label: "Team Members", icon: <People /> },
    { value: "6", label: "Open Positions", icon: <Work /> },
    { value: "4.8★", label: "Glassdoor Rating", icon: <Star /> },
    { value: "₹50Cr+", label: "Revenue Run Rate", icon: <TrendingUp /> },
  ];

  const filteredJobs = jobs.filter((job) => {
    const matchesDept = selectedDept === "All" || job.department === selectedDept;
    const matchesSearch =
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.location.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesDept && matchesSearch;
  });

  const handleSave = (id) => {
    setSavedJobs((prev) =>
      prev.includes(id) ? prev.filter((j) => j !== id) : [...prev, id]
    );
  };

  const handleApply = (job) => {
    setSelectedJob(job);
    setDetailOpen(false);
    setApplyOpen(true);
  };

  const modeColor = (mode) => {
    if (mode === "Remote") return "#10b981";
    if (mode === "Hybrid") return "#0ea5e9";
    return "#f59e0b";
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>

      {/* ── HERO ── */}
      <Box
        sx={{
          background: "linear-gradient(160deg, #020617 0%, #0f0a2e 30%, #1a0533 55%, #050d1f 100%)",
          position: "relative",
          overflow: "hidden",
          py: { xs: 10, md: 16 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Dot-grid bg */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: "radial-gradient(rgba(139,92,246,0.18) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        {/* Top-left aurora */}
        <Box sx={{
          position: "absolute", width: 700, height: 700, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139,92,246,0.45) 0%, rgba(99,102,241,0.2) 40%, transparent 70%)",
          top: "-25%", left: "-10%", filter: "blur(90px)",
          animation: "floatA 20s ease-in-out infinite",
          "@keyframes floatA": { "0%,100%": { transform: "translate(0,0) scale(1)" }, "50%": { transform: "translate(40px, 60px) scale(1.1)" } },
        }} />
        {/* Bottom-right aurora */}
        <Box sx={{
          position: "absolute", width: 600, height: 600, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(236,72,153,0.4) 0%, rgba(217,70,239,0.15) 40%, transparent 70%)",
          bottom: "-20%", right: "-5%", filter: "blur(90px)",
          animation: "floatB 16s ease-in-out infinite reverse",
          "@keyframes floatB": { "0%,100%": { transform: "translate(0,0)" }, "50%": { transform: "translate(-30px, -40px)" } },
        }} />
        {/* Centre glow */}
        <Box sx={{
          position: "absolute", width: 500, height: 300, borderRadius: "50%",
          background: "radial-gradient(ellipse, rgba(99,102,241,0.25) 0%, transparent 70%)",
          top: "30%", left: "50%", transform: "translateX(-50%)", filter: "blur(60px)",
        }} />

        {/* Decorative horizontal rule lines */}
        <Box sx={{ position: "absolute", top: "18%", left: 0, right: 0, height: "1px", background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.25), transparent)" }} />
        <Box sx={{ position: "absolute", bottom: "15%", left: 0, right: 0, height: "1px", background: "linear-gradient(90deg, transparent, rgba(236,72,153,0.2), transparent)" }} />

        <Container sx={{ position: "relative", zIndex: 2 }}>
          <Box sx={{ textAlign: "center", maxWidth: 860, mx: "auto" }}>

            {/* Badge */}
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, mb: 5,
              bgcolor: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.35)",
              borderRadius: "100px", px: 2.5, py: 1,
            }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#a855f7",
                boxShadow: "0 0 8px rgba(168,85,247,0.9)",
                animation: "blink 1.8s ease-in-out infinite",
                "@keyframes blink": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
              }} />
              <AutoAwesome sx={{ color: "#c084fc", fontSize: 16 }} />
              <Typography sx={{ color: "#c084fc", fontWeight: 700, fontSize: "0.92rem", letterSpacing: "0.03em" }}>
                We're Hiring — Join OruClick
              </Typography>
            </Box>

            {/* Main heading */}
            <Box sx={{ mb: 4 }}>
              {/* Line 1 — plain white */}
              <Typography
                component="div"
                sx={{
                  fontSize: { xs: "2.8rem", sm: "4.2rem", md: "6.5rem" },
                  fontWeight: 900,
                  color: "white",
                  lineHeight: 1,
                  letterSpacing: { xs: "-0.02em", md: "-0.035em" },
                  mb: { xs: 0.5, md: 1 },
                }}
              >
                Build the{" "}
                {/* "Future" with neon outline box */}
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    position: "relative",
                    px: { xs: 1, md: 2 },
                    py: { xs: 0, md: 0.5 },
                    background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      borderRadius: "12px",
                      border: "2px solid",
                      borderColor: "rgba(168,85,247,0.5)",
                      background: "rgba(139,92,246,0.08)",
                    },
                  }}
                >
                  Future
                </Box>
              </Typography>

              {/* Line 2 — "with OruClick" split treatment */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: { xs: 1.5, md: 3 }, flexWrap: "wrap" }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: "2.8rem", sm: "4.2rem", md: "6.5rem" },
                    fontWeight: 900,
                    color: "rgba(255,255,255,0.45)",
                    lineHeight: 1,
                    letterSpacing: { xs: "-0.02em", md: "-0.035em" },
                  }}
                >
                  with
                </Typography>

                {/* Divider dot */}
                <Box sx={{
                  width: { xs: 8, md: 14 }, height: { xs: 8, md: 14 }, borderRadius: "50%",
                  background: "linear-gradient(135deg, #a855f7, #ec4899)",
                  boxShadow: "0 0 20px rgba(168,85,247,0.8)",
                  flexShrink: 0,
                }} />

                {/* "OruClick" — animated gradient shimmer */}
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: "2.8rem", sm: "4.2rem", md: "6.5rem" },
                    fontWeight: 900,
                    lineHeight: 1,
                    letterSpacing: { xs: "-0.02em", md: "-0.035em" },
                    background: "linear-gradient(90deg, #a855f7 0%, #ec4899 25%, #f97316 50%, #eab308 70%, #a855f7 100%)",
                    backgroundSize: "300% 100%",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    animation: "shimmer 4s linear infinite",
                    "@keyframes shimmer": {
                      "0%": { backgroundPosition: "0% 50%" },
                      "100%": { backgroundPosition: "300% 50%" },
                    },
                  }}
                >
                  OruClick
                </Typography>
              </Box>

              {/* Underline accent below heading */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 2, md: 3 }, gap: 1 }}>
                <Box sx={{ height: 4, width: { xs: 60, md: 100 }, borderRadius: 2, background: "linear-gradient(90deg, transparent, #7c3aed)" }} />
                <Box sx={{ height: 4, width: { xs: 30, md: 50 }, borderRadius: 2, background: "linear-gradient(90deg, #a855f7, #ec4899)" }} />
                <Box sx={{ height: 4, width: { xs: 15, md: 24 }, borderRadius: 2, background: "linear-gradient(90deg, #ec4899, transparent)" }} />
              </Box>
            </Box>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.75)",
                fontSize: { xs: "1rem", md: "1.25rem" },
                lineHeight: 1.7,
                mb: 5,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              We're a fast-growing B2B SaaS company building powerful CRM tools for
              businesses across the world. Come solve real problems, ship fast, and grow
              your career alongside exceptional teammates.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => {
                  document.getElementById("open-roles")?.scrollIntoView({ behavior: "smooth" });
                }}
                sx={{
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  color: "white",
                  px: 5,
                  py: 2,
                  fontSize: "1.05rem",
                  fontWeight: 800,
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: "0 20px 60px rgba(99,102,241,0.45)",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 28px 70px rgba(99,102,241,0.6)",
                  },
                  transition: "all 0.3s",
                }}
              >
                View Open Roles
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: "rgba(255,255,255,0.3)",
                  color: "white",
                  px: 5,
                  py: 2,
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: "none",
                  backdropFilter: "blur(10px)",
                  bgcolor: "rgba(255,255,255,0.05)",
                  borderWidth: 2,
                  "&:hover": {
                    borderColor: "#8b5cf6",
                    bgcolor: "rgba(99,102,241,0.15)",
                    borderWidth: 2,
                    transform: "translateY(-3px)",
                  },
                  transition: "all 0.3s",
                }}
              >
                Our Culture
              </Button>
            </Stack>
          </Box>

          {/* Stats Row */}
          <Grid container spacing={3} sx={{ mt: 8 }} justifyContent="center">
            {stats.map((stat, i) => (
              <Grid key={i} item xs={6} sm={3}>
                <Paper
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 3,
                    p: 3,
                    textAlign: "center",
                    backdropFilter: "blur(10px)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.1)", transform: "translateY(-4px)" },
                    transition: "all 0.3s",
                  }}
                >
                  <Box sx={{ color: "#a5b4fc", mb: 1 }}>{stat.icon}</Box>
                  <Typography sx={{ color: "white", fontWeight: 900, fontSize: "1.8rem" }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── WHY ORUCLICKS ── */}
      <Box sx={{ bgcolor: "white", py: { xs: 8, md: 12 } }}>
        <Container>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="Why OruClick?"
              sx={{ bgcolor: "#ede9fe", color: "#6d28d9", fontWeight: 700, mb: 2, fontSize: "0.85rem" }}
            />
            <Typography variant="h3" sx={{ fontWeight: 900, color: "#0f172a", fontSize: { xs: "2rem", md: "3rem" }, mb: 2 }}>
              A place where you can
              <Box component="span" sx={{ color: "#6366f1" }}> thrive</Box>
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: "1.1rem", maxWidth: 560, mx: "auto" }}>
              We build a company where talented people do their best work — supported, challenged, and rewarded.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              {
                icon: <TrendingUp sx={{ fontSize: 32, color: "#6366f1" }} />,
                title: "High-Impact Work",
                desc: "Every feature you ship is used by thousands of real businesses. Your work matters from day one.",
                bg: "#ede9fe",
              },
              {
                icon: <Groups sx={{ fontSize: 32, color: "#0ea5e9" }} />,
                title: "World-Class Team",
                desc: "Work alongside alumni from IITs, NITs, top startups, and global tech companies.",
                bg: "#e0f2fe",
              },
              {
                icon: <WorkHistory sx={{ fontSize: 32, color: "#10b981" }} />,
                title: "Career Growth",
                desc: "Bi-annual promotions, clear career ladders, and mentorship from experienced leaders.",
                bg: "#d1fae5",
              },
              {
                icon: <Favorite sx={{ fontSize: 32, color: "#ef4444" }} />,
                title: "Great Culture",
                desc: "Async-friendly, low politics, high trust. We celebrate wins and learn from failures openly.",
                bg: "#fee2e2",
              },
            ].map((item, i) => (
              <Grid key={i} item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    border: "1.5px solid #f1f5f9",
                    borderRadius: 3,
                    p: 4,
                    height: "100%",
                    "&:hover": {
                      borderColor: "#c7d2fe",
                      boxShadow: "0 12px 40px rgba(99,102,241,0.12)",
                      transform: "translateY(-4px)",
                    },
                    transition: "all 0.3s",
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2.5,
                      bgcolor: item.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2.5,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.1rem", mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: "#64748b", lineHeight: 1.65 }}>{item.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── OPEN ROLES ── */}
      <Box id="open-roles" sx={{ bgcolor: "#f8fafc", py: { xs: 8, md: 12 } }}>
        <Container>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Chip
              label="Open Positions"
              sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 700, mb: 2, fontSize: "0.85rem" }}
            />
            <Typography variant="h3" sx={{ fontWeight: 900, color: "#0f172a", fontSize: { xs: "2rem", md: "3rem" }, mb: 2 }}>
              Find your <Box component="span" sx={{ color: "#6366f1" }}>perfect role</Box>
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: "1.05rem" }}>
              {filteredJobs.length} open position{filteredJobs.length !== 1 ? "s" : ""} across all departments
            </Typography>
          </Box>

          {/* Search + Filter */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 4 }}
            alignItems={{ sm: "center" }}
          >
            <TextField
              fullWidth
              placeholder="Search roles, skills, locations…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: "white",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "& fieldset": { borderColor: "#e2e8f0" },
                  "&:hover fieldset": { borderColor: "#6366f1" },
                  "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                },
              }}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", minWidth: { sm: "auto" } }}>
              {departments.map((dept) => (
                <Chip
                  key={dept}
                  label={dept}
                  onClick={() => setSelectedDept(dept)}
                  sx={{
                    fontWeight: 600,
                    cursor: "pointer",
                    bgcolor: selectedDept === dept ? "#6366f1" : "white",
                    color: selectedDept === dept ? "white" : "#475569",
                    border: "1.5px solid",
                    borderColor: selectedDept === dept ? "#6366f1" : "#e2e8f0",
                    "&:hover": { bgcolor: selectedDept === dept ? "#4f46e5" : "#f1f5f9" },
                    transition: "all 0.2s",
                  }}
                />
              ))}
            </Box>
          </Stack>

          {/* Job Cards */}
          {filteredJobs.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <FilterList sx={{ fontSize: 60, color: "#cbd5e1", mb: 2 }} />
              <Typography sx={{ color: "#94a3b8", fontSize: "1.1rem" }}>
                No roles found. Try changing your search or filter.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredJobs.map((job) => (
                <Grid key={job.id} item xs={12} md={6}>
                  <Fade in timeout={400}>
                    <Paper
                      elevation={0}
                      sx={{
                        border: "1.5px solid #e2e8f0",
                        borderRadius: 3,
                        p: { xs: 3, md: 4 },
                        cursor: "pointer",
                        position: "relative",
                        bgcolor: "white",
                        "&:hover": {
                          borderColor: job.color,
                          boxShadow: `0 16px 48px ${job.color}22`,
                          transform: "translateY(-4px)",
                        },
                        transition: "all 0.3s",
                        borderLeft: `5px solid ${job.color}`,
                      }}
                      onClick={() => {
                        setSelectedJob(job);
                        setDetailOpen(true);
                      }}
                    >
                      {job.urgent && (
                        <Chip
                          label="Urgent Hire"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 16,
                            right: 52,
                            bgcolor: "#fef3c7",
                            color: "#d97706",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            animation: "pulse 2s ease infinite",
                            "@keyframes pulse": {
                              "0%,100%": { opacity: 1 },
                              "50%": { opacity: 0.75 },
                            },
                          }}
                        />
                      )}

                      <Tooltip title={savedJobs.includes(job.id) ? "Unsave" : "Save job"}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSave(job.id);
                          }}
                          sx={{ position: "absolute", top: 12, right: 12 }}
                        >
                          {savedJobs.includes(job.id) ? (
                            <Bookmark sx={{ color: "#6366f1", fontSize: 20 }} />
                          ) : (
                            <BookmarkBorder sx={{ color: "#cbd5e1", fontSize: 20 }} />
                          )}
                        </IconButton>
                      </Tooltip>

                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: `${job.color}20`,
                            color: job.color,
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            fontWeight: 800,
                            fontSize: "1rem",
                          }}
                        >
                          {job.department.slice(0, 2).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.1rem", lineHeight: 1.3 }}>
                            {job.title}
                          </Typography>
                          <Typography sx={{ color: "#64748b", fontSize: "0.85rem", fontWeight: 600 }}>
                            {job.department}
                          </Typography>
                        </Box>
                      </Box>

                      <Typography sx={{ color: "#475569", fontSize: "0.9rem", mb: 2.5, lineHeight: 1.6 }}>
                        {job.description.slice(0, 120)}…
                      </Typography>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                        <Chip
                          icon={<LocationOn sx={{ fontSize: 14, color: "#64748b !important" }} />}
                          label={job.location}
                          size="small"
                          sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: "0.78rem" }}
                        />
                        <Chip
                          icon={<Schedule sx={{ fontSize: 14, color: "#64748b !important" }} />}
                          label={job.type}
                          size="small"
                          sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: "0.78rem" }}
                        />
                        <Chip
                          label={job.mode}
                          size="small"
                          sx={{
                            bgcolor: `${modeColor(job.mode)}15`,
                            color: modeColor(job.mode),
                            fontWeight: 700,
                            fontSize: "0.78rem",
                          }}
                        />
                        <Chip
                          icon={<CurrencyRupee sx={{ fontSize: 13, color: "#64748b !important" }} />}
                          label={job.salary}
                          size="small"
                          sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: "0.78rem" }}
                        />
                      </Box>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ color: "#94a3b8", fontSize: "0.8rem" }}>
                          Posted {job.posted}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApply(job);
                          }}
                          sx={{
                            bgcolor: job.color,
                            fontSize: "0.82rem",
                            fontWeight: 700,
                            textTransform: "none",
                            borderRadius: 1.5,
                            px: 2.5,
                            "&:hover": { bgcolor: job.color, filter: "brightness(0.9)" },
                          }}
                        >
                          Apply Now
                        </Button>
                      </Stack>
                    </Paper>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* ── PERKS & BENEFITS ── */}
      <Box sx={{ bgcolor: "white", py: { xs: 8, md: 12 } }}>
        <Container>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="Perks & Benefits"
              sx={{ bgcolor: "#fce7f3", color: "#9d174d", fontWeight: 700, mb: 2, fontSize: "0.85rem" }}
            />
            <Typography variant="h3" sx={{ fontWeight: 900, color: "#0f172a", fontSize: { xs: "2rem", md: "3rem" }, mb: 2 }}>
              Everything you need to <Box component="span" sx={{ color: "#6366f1" }}>do great work</Box>
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: "1.1rem" }}>
              We invest heavily in our team's wellbeing, growth, and quality of life.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {perks.map((perk, i) => (
              <Grid key={i} item xs={12} sm={6} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    border: "1.5px solid #f1f5f9",
                    borderRadius: 3,
                    p: 4,
                    height: "100%",
                    "&:hover": {
                      borderColor: `${perk.color}55`,
                      boxShadow: `0 12px 40px ${perk.color}18`,
                      transform: "translateY(-4px)",
                    },
                    transition: "all 0.3s",
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 3,
                      bgcolor: `${perk.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: perk.color,
                      mb: 2.5,
                    }}
                  >
                    {perk.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.05rem", mb: 1 }}>
                    {perk.title}
                  </Typography>
                  <Typography sx={{ color: "#64748b", lineHeight: 1.65 }}>{perk.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── EMPLOYEE STORIES ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #0f172a 100%)",
          py: { xs: 8, md: 12 },
          px: { xs: 2, sm: 3 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(99,102,241,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.04) 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        />
        <Container sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="Life at OruClick"
              sx={{ bgcolor: "rgba(99,102,241,0.2)", color: "#a5b4fc", fontWeight: 700, mb: 2 }}
            />
            <Typography
              variant="h3"
              sx={{ fontWeight: 900, color: "white", fontSize: { xs: "2rem", md: "3rem" }, mb: 2 }}
            >
              Hear from our <Box component="span" sx={{ color: "#a5b4fc" }}>team</Box>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testimonials.map((t, i) => (
              <Grid key={i} item xs={12} md={4}>
                <Paper
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 3,
                    p: 4,
                    backdropFilter: "blur(10px)",
                    height: "100%",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.1)", transform: "translateY(-4px)" },
                    transition: "all 0.3s",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Avatar sx={{ bgcolor: t.color, width: 48, height: 48, fontWeight: 800 }}>
                      {t.avatar}
                    </Avatar>
                    <Box>
                      <Typography sx={{ color: "white", fontWeight: 700 }}>{t.name}</Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem" }}>
                        {t.role} · {t.joined}
                      </Typography>
                    </Box>
                    <Verified sx={{ color: t.color, ml: "auto !important" }} />
                  </Stack>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} sx={{ color: "#fbbf24", fontSize: 18 }} />
                    ))}
                  </Box>
                  <Typography
                    sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.7, fontStyle: "italic" }}
                  >
                    "{t.text}"
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── BOTTOM CTA ── */}
      <Box sx={{ bgcolor: "#f8fafc", py: { xs: 8, md: 10 }, px: { xs: 2, sm: 3 } }}>
        <Container>
          <Paper
            elevation={0}
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)",
              borderRadius: 4,
              p: { xs: 5, md: 8 },
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 400,
                height: 400,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.08)",
                top: "-20%",
                right: "-5%",
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                color: "white",
                fontSize: { xs: "1.8rem", md: "2.8rem" },
                mb: 2,
                position: "relative",
                zIndex: 1,
              }}
            >
              Don't see the right role?
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: "1.1rem",
                mb: 4,
                maxWidth: 500,
                mx: "auto",
                position: "relative",
                zIndex: 1,
              }}
            >
              We're always looking for talented people. Send us your resume and we'll
              reach out when a suitable role opens up.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<Send />}
              onClick={() => {
                setSelectedJob({ title: "General Application", department: "Any", color: "#6366f1" });
                setApplyOpen(true);
              }}
              sx={{
                bgcolor: "white",
                color: "#6366f1",
                px: 5,
                py: 2,
                fontSize: "1.05rem",
                fontWeight: 800,
                borderRadius: 2,
                textTransform: "none",
                position: "relative",
                zIndex: 1,
                "&:hover": { bgcolor: "#f5f3ff", transform: "translateY(-3px)" },
                transition: "all 0.3s",
              }}
            >
              Send Open Application
            </Button>
          </Paper>
        </Container>
      </Box>

      <Footer />

      {/* ── JOB DETAIL DIALOG ── */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
      >
        {selectedJob && (
          <>
            <DialogTitle
              sx={{
                background: `linear-gradient(135deg, ${selectedJob.color}22, ${selectedJob.color}08)`,
                borderBottom: "1px solid #f1f5f9",
                p: 3,
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: "#0f172a" }}>
                    {selectedJob.title}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                    <Chip label={selectedJob.department} size="small" sx={{ bgcolor: `${selectedJob.color}20`, color: selectedJob.color, fontWeight: 700 }} />
                    <Chip icon={<LocationOn sx={{ fontSize: 13 }} />} label={selectedJob.location} size="small" sx={{ bgcolor: "#f1f5f9", color: "#475569" }} />
                    <Chip label={selectedJob.mode} size="small" sx={{ bgcolor: `${modeColor(selectedJob.mode)}15`, color: modeColor(selectedJob.mode), fontWeight: 700 }} />
                    <Chip label={selectedJob.salary} size="small" sx={{ bgcolor: "#f1f5f9", color: "#475569" }} />
                  </Stack>
                </Box>
                <IconButton onClick={() => setDetailOpen(false)} size="small">
                  <Close />
                </IconButton>
              </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Typography sx={{ color: "#475569", lineHeight: 1.75, mb: 3 }}>
                {selectedJob.description}
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Typography sx={{ fontWeight: 800, color: "#0f172a", mb: 1.5 }}>
                Responsibilities
              </Typography>
              {selectedJob.responsibilities?.map((r, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
                  <CheckCircle sx={{ color: selectedJob.color, fontSize: 18, mt: 0.3 }} />
                  <Typography sx={{ color: "#475569" }}>{r}</Typography>
                </Stack>
              ))}

              <Typography sx={{ fontWeight: 800, color: "#0f172a", mt: 3, mb: 1.5 }}>
                Requirements
              </Typography>
              {selectedJob.requirements?.map((r, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
                  <CheckCircle sx={{ color: "#10b981", fontSize: 18, mt: 0.3 }} />
                  <Typography sx={{ color: "#475569" }}>{r}</Typography>
                </Stack>
              ))}

              {selectedJob.niceToHave && (
                <>
                  <Typography sx={{ fontWeight: 800, color: "#0f172a", mt: 3, mb: 1.5 }}>
                    Nice to Have
                  </Typography>
                  {selectedJob.niceToHave.map((r, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
                      <Star sx={{ color: "#f59e0b", fontSize: 18, mt: 0.3 }} />
                      <Typography sx={{ color: "#475569" }}>{r}</Typography>
                    </Stack>
                  ))}
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: "1px solid #f1f5f9", gap: 1 }}>
              <Button
                onClick={() => setDetailOpen(false)}
                sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                endIcon={<Send />}
                onClick={() => handleApply(selectedJob)}
                sx={{
                  bgcolor: selectedJob.color,
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  "&:hover": { bgcolor: selectedJob.color, filter: "brightness(0.9)" },
                }}
              >
                Apply for this Role
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── APPLY DIALOG ── */}
      <ApplyDialog
        open={applyOpen}
        job={selectedJob}
        onClose={() => setApplyOpen(false)}
        onSuccess={() => { setApplyOpen(false); setSuccessOpen(true); }}
      />

      {/* ── SUCCESS DIALOG ── */}
      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, textAlign: "center" } }}
      >
        <DialogContent sx={{ p: 5 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "#d1fae5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <CheckCircle sx={{ fontSize: 44, color: "#10b981" }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: "#0f172a", mb: 1 }}>
            Application Submitted!
          </Typography>
          <Typography sx={{ color: "#64748b", lineHeight: 1.7, mb: 3 }}>
            Thank you for applying to{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#6366f1" }}>
              {selectedJob?.title}
            </Box>{" "}
            at OruClick. Our team will review your profile and get back to you within{" "}
            <strong>3–5 business days</strong>.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setSuccessOpen(false)}
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              py: 1.5,
            }}
          >
            Back to Careers
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LandingPage;
